import React, { useCallback, useMemo, useState } from 'react';
import { Modal, Pressable, RefreshControl, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { confirmDestructive } from '../../ui/dialogs';
import { createChatClient } from '../../runtime/mobile/chatClient';
import { colors, radius, spacing, typography } from '../../config/theme';
import { presenceStatus } from '../../runtime/presence';
import { Card, ErrorBanner, Field, Loader, PrimaryButton, TextButton, screen } from '../ui';

const toMs = (v) => (v ? new Date(v).getTime() || 0 : 0);
const timeLabel = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const now = new Date();
  return d.toDateString() === now.toDateString()
    ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    : `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const icon = (kind) => (kind === 'location' ? '🏪' : kind === 'group' ? '👥' : '👤');

export default function ChatListScreen({ navigation }) {
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const { channels, unread, refresh, notifPerm, askNotifications, markRead, clearChannel } = useChat();
  const client = useMemo(() => createChatClient(user), [user]);

  const [refreshing, setRefreshing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [mode, setMode] = useState('dm'); // 'dm' | 'group'
  const [people, setPeople] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [groupTitle, setGroupTitle] = useState('');
  const [groupSel, setGroupSel] = useState(() => new Set());

  const ordered = useMemo(
    () => [...channels].sort((a, b) => toMs(b.lastMessageAt) - toMs(a.lastMessageAt)),
    [channels],
  );

  const openThread = (c) => {
    markRead(c.id);
    navigation.navigate('ChatThread', { channelId: c.id, title: c.title });
  };

  const deleteChat = (c) => confirmDestructive(
    'Удалить чат?',
    'Он исчезнет из вашего списка. У собеседника всё останется. Чат вернётся, если вам снова напишут.',
    'Удалить',
    () => clearChannel(c.id),
  );

  const openPicker = async () => {
    setPicking(true);
    setMode('dm');
    setErr(null);
    setGroupTitle('');
    setGroupSel(new Set());
    if (!people) {
      try { setPeople(await client.listDirectory()); } catch { setPeople([]); setErr('Не удалось загрузить список'); }
    }
  };

  const startDm = async (person) => {
    setBusy(person.uid);
    setErr(null);
    try {
      const res = await client.openDirect(person.uid);
      const channelId = res?.data?.channelId || res?.channelId;
      setPicking(false);
      await refresh();
      navigation.navigate('ChatThread', { channelId, title: person.fullName });
    } catch {
      setErr('Не получилось открыть чат');
    } finally {
      setBusy(null);
    }
  };

  const toggleMember = (uid) => setGroupSel((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  const createGroup = async () => {
    const title = groupTitle.trim();
    if (!title) { setErr('Введите название группы'); return; }
    if (!groupSel.size) { setErr('Отметьте хотя бы одного участника'); return; }
    setBusy('group');
    setErr(null);
    try {
      const res = await client.createGroup({ title, memberIds: [...groupSel] });
      const channelId = res?.data?.channelId || res?.channelId;
      setPicking(false);
      await refresh();
      navigation.navigate('ChatThread', { channelId, title });
    } catch (e) {
      setErr(e?.serverMessage || 'Не удалось создать группу');
    } finally {
      setBusy(null);
    }
  };

  const groupPeople = (people || []).filter((p) => p.role !== 'owner');
  const doRefresh = useCallback(async () => { setRefreshing(true); await refresh(); setRefreshing(false); }, [refresh]);

  if (!channels.length && !ordered.length) {
    // still nothing after first poll — show a light empty state, not a spinner
  }

  return (
    <View style={screen.root}>
      <ScrollView
        contentContainerStyle={[screen.content, { paddingTop: insets.top + spacing.xl }]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={doRefresh} tintColor={colors.primary} />}
      >
        <View style={styles.head}>
          <Text style={styles.h}>Чат</Text>
          <TextButton title="＋ Новый чат" onPress={openPicker} />
        </View>
        {notifPerm === 'default' && (
          <Card onPress={askNotifications} style={styles.notifCard}>
            <Text style={styles.notifText}>🔔 Включить уведомления о новых сообщениях</Text>
          </Card>
        )}
        {!ordered.length && <Text style={styles.sub}>Каналы появятся автоматически: общий чат точки и переписка с владельцем.</Text>}
        {ordered.map((c) => {
          const n = unread[c.id] || 0;
          const seen = c.kind === 'direct' ? presenceStatus(c.otherLastSeenAt) : null;
          return (
            <Card key={c.id} onPress={() => openThread(c)} onLongPress={() => deleteChat(c)} style={styles.row}>
              <View>
                <Text style={styles.icon}>{icon(c.kind)}</Text>
                {seen?.online ? <View style={styles.presenceDot} /> : null}
              </View>
              <View style={styles.grow}>
                <Text style={[styles.title, n > 0 && styles.titleUnread]}>{c.title}</Text>
                <Text style={[styles.preview, n > 0 && styles.previewUnread]} numberOfLines={1}>
                  {c.lastMessageAuthor ? `${c.lastMessageAuthor}: ` : ''}{c.lastMessagePreview || 'Нет сообщений'}
                </Text>
                {seen ? (
                  <Text style={[styles.presenceLine, seen.online && styles.presenceOnline]}>
                    {seen.online ? '● в сети' : `был(а) в сети ${seen.text}`}
                  </Text>
                ) : null}
              </View>
              <View style={styles.rightCol}>
                <Text style={styles.time}>{timeLabel(toMs(c.lastMessageAt))}</Text>
                {n > 0 && (
                  <View style={styles.badge}><Text style={styles.badgeText}>{n > 99 ? '99+' : n}</Text></View>
                )}
              </View>
            </Card>
          );
        })}
      </ScrollView>

      <Modal visible={picking} animationType="slide" transparent onRequestClose={() => setPicking(false)}>
        <View style={styles.sheetWrap}>
          <View style={styles.sheet}>
            <View style={styles.head}>
              <Text style={styles.sheetTitle}>Новый чат</Text>
              <TextButton title="Закрыть" kind="muted" onPress={() => setPicking(false)} />
            </View>

            <View style={styles.segment}>
              {[['dm', 'Личный'], ['group', 'Группа']].map(([k, label]) => (
                <Pressable key={k} onPress={() => { setMode(k); setErr(null); }} style={[styles.seg, mode === k && styles.segOn]}>
                  <Text style={[styles.segText, mode === k && styles.segTextOn]}>{label}</Text>
                </Pressable>
              ))}
            </View>

            <ErrorBanner message={err} />

            {mode === 'group' && (
              <>
                <Field label="Название группы" value={groupTitle} onChangeText={setGroupTitle} placeholder="Смена «Якобы табак»" />
                <Text style={styles.hint}>Владелец видит все группы автоматически.</Text>
              </>
            )}

            <ScrollView contentContainerStyle={{ gap: spacing.xs }} keyboardShouldPersistTaps="handled" style={{ marginTop: spacing.xs }}>
              {people === null && <Loader />}
              {mode === 'dm' && people !== null && !people.length && <Text style={styles.sub}>Пока некому — нет других сотрудников на твоих точках.</Text>}
              {mode === 'group' && people !== null && !groupPeople.length && <Text style={styles.sub}>Пока некого добавить — нет других сотрудников на твоих точках.</Text>}
              {mode === 'dm' && (people || []).map((p) => (
                <Pressable key={p.uid} onPress={() => startDm(p)} disabled={!!busy} style={styles.person}>
                  <Text style={styles.personIcon}>{p.role === 'owner' ? '👑' : '👤'}</Text>
                  <Text style={styles.personName}>{p.fullName}</Text>
                  {busy === p.uid && <Text style={styles.personHint}>…</Text>}
                </Pressable>
              ))}
              {mode === 'group' && groupPeople.map((p) => {
                const on = groupSel.has(p.uid);
                return (
                  <Pressable key={p.uid} onPress={() => toggleMember(p.uid)} style={[styles.person, on && styles.personOn]}>
                    <Text style={styles.personIcon}>{on ? '☑️' : '⬜️'}</Text>
                    <Text style={styles.personName}>{p.fullName}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>

            {mode === 'group' && (
              <PrimaryButton
                title={groupSel.size ? `Создать группу (${groupSel.size})` : 'Создать группу'}
                onPress={createGroup}
                loading={busy === 'group'}
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  h: { ...typography.h1, color: colors.textPrimary },
  grow: { flex: 1 },
  sub: { ...typography.body, color: colors.textSecondary, marginTop: spacing.xs },
  hint: { ...typography.caption, color: colors.textMuted, marginTop: 4 },
  notifCard: { alignItems: 'center', paddingVertical: spacing.md },
  notifText: { ...typography.body, color: colors.primary, fontWeight: '700', fontSize: 13 },
  sheetWrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, maxHeight: '85%', gap: spacing.sm },
  sheetTitle: { ...typography.h3, color: colors.textPrimary },
  segment: { flexDirection: 'row', gap: spacing.xs, backgroundColor: colors.surfaceCard, borderRadius: radius.sm, padding: 3 },
  seg: { flex: 1, paddingVertical: spacing.sm, alignItems: 'center', borderRadius: radius.sm - 2 },
  segOn: { backgroundColor: 'rgba(0,210,160,0.14)' },
  segText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  segTextOn: { color: colors.primary },
  person: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  personOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,210,160,0.10)' },
  personIcon: { fontSize: 18, width: 26, textAlign: 'center' },
  personName: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14, flex: 1 },
  personHint: { ...typography.caption, color: colors.textMuted },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  icon: { fontSize: 22, width: 30, textAlign: 'center' },
  presenceDot: {
    position: 'absolute', right: 0, bottom: 0, width: 11, height: 11, borderRadius: 6,
    backgroundColor: colors.success, borderWidth: 2, borderColor: colors.surfaceCard,
  },
  presenceLine: { ...typography.caption, color: colors.textMuted, fontSize: 11, marginTop: 2 },
  presenceOnline: { color: colors.success, fontWeight: '700' },
  title: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14 },
  titleUnread: { color: colors.primary },
  preview: { ...typography.caption, color: colors.textMuted, marginTop: 2 },
  previewUnread: { color: colors.textSecondary, fontWeight: '700' },
  rightCol: { alignItems: 'flex-end', gap: 4 },
  time: { ...typography.caption, color: colors.textMuted, fontVariant: ['tabular-nums'] },
  badge: { minWidth: 20, height: 20, borderRadius: 10, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 5 },
  badgeText: { color: colors.onPrimary, fontSize: 11, fontWeight: '900' },
});
